import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { useMutation, useQuery } from '@apollo/client'
import { Alert, Box, Button, Grid } from '@mui/material'
import { Add as AddIcon } from '@mui/icons-material'
import SectionAccordion from 'gui/components/accordion/section-accordion'
import {
  CONTENT_REFERENCE_TYPE_COURSE_MODULE_PRACTICE,
  CONTENT_REFERENCE_TYPE_COURSE_MODULE_OTHER,
} from 'services/content-references/types'
import ContentAutocomplete from 'gui/components/common/ContentAutocomplete'
import CourseModuleContentCard from './course-module-content-card'
import type { ContentReference, CourseModuleContentReference } from './types'
import {
  GET_COURSE_MODULE_CONTENT_REFERENCES,
  UPDATE_CONTENT_REFERENCES_SORT_ORDER,
} from './queries'
import { DELETE_CONTENT_REFERENCE } from './content-references/queries'
import './course-module-content-section.scss'

interface CourseModuleContentSectionProps {
  /** Course Module UUID for fetching data */
  moduleId: string
  /** Course Module contentId (numeric) for content reference creation */
  moduleContentId: number
  /** Course Module language for fetching content (e.g., "en", "de") */
  moduleLanguage: string
  /** Whether the section is disabled (view-only mode) */
  disabled?: boolean
  /** Callback when dirty state changes */
  onDirtyChange?: (isDirty: boolean) => void
  /** Callback to get the save handler */
  onSaveHandler?: (saveHandler: () => Promise<void>) => void
  /** Reset trigger counter - incremented by parent to trigger reset */
  onResetTriggered?: number
  /** Callback when content is added via autocomplete */
  onContentAdded?: (contentReference: ContentReference, type: string) => void
}

/**
 * Course Module Content Section Component
 * Displays practice and other content linked to a Course Module
 * Fetches and manages its own data
 */
function CourseModuleContentSection (props: CourseModuleContentSectionProps) {
  const {
    disabled = false,
    moduleContentId,
    moduleId,
    moduleLanguage,
    onDirtyChange,
    onSaveHandler,
    onResetTriggered = 0,
    onContentAdded,
  } = props

  // Fetch course module content references
  const { loading, error, data } = useQuery(GET_COURSE_MODULE_CONTENT_REFERENCES, {
    variables: {
      id: moduleId,
      language: [moduleLanguage, '_primary'],
    },
    skip: !moduleId || !moduleLanguage,
  })

  // Extract content references from query data (memoized to avoid dependency issues)
  const serverCourseModuleContentReferences = useMemo(
    () => data?.contentList?.content?.[0]?.contentReferencesTo || [],
    [data],
  )

  // Track course module content reference IDs to delete
  const [courseModuleContentReferenceIdsToDelete, setCourseModuleContentReferenceIdsToDelete] =
    useState<Set<string>>(new Set())

  // Track content references to create
  const [contentReferencesToCreate, setContentReferencesToCreate] = useState<
    Array<{
      contentReference: ContentReference
      type: string
    }>
  >([])

  // State for tracking edited sort keys (currently being typed, not confirmed)
  // Map of contentReferenceId -> edited sort key value (as string)
  const [editedSortKeys, setEditedSortKeys] = useState<Record<string, string>>({})

  // State for locally reordered content references (confirmed by user, waiting to be saved)
  const [locallyReorderedContentReferences, setLocallyReorderedContentReferences] = useState<
    CourseModuleContentReference[] | null
  >(null)

  // State for adding new content
  const [isAddingContent, setIsAddingContent] = useState<boolean>(false)
  const [selectedContentToAdd, setSelectedContentToAdd] = useState<ContentReference[]>([])
  const [contentReferenceTypeToAdd, setContentReferenceTypeToAdd] = useState<string>(
    CONTENT_REFERENCE_TYPE_COURSE_MODULE_PRACTICE,
  )

  // Handle clicking Add Practice button
  const handleAddPracticeClick = useCallback(() => {
    setContentReferenceTypeToAdd(CONTENT_REFERENCE_TYPE_COURSE_MODULE_PRACTICE)
    setIsAddingContent(true)
  }, [])

  // Handle clicking Add Other button
  const handleAddOtherClick = useCallback(() => {
    setContentReferenceTypeToAdd(CONTENT_REFERENCE_TYPE_COURSE_MODULE_OTHER)
    setIsAddingContent(true)
  }, [])

  // Detect refs with null/undefined sortKey (from production) so we can repair on save
  const hasNullSortKeys = useMemo(
    () =>
      serverCourseModuleContentReferences.some(
        (ref: CourseModuleContentReference) => ref.sortKey === null || ref.sortKey === undefined,
      ),
    [serverCourseModuleContentReferences],
  )

  // Notify parent when dirty state changes
  useEffect(() => {
    const isDirty =
      courseModuleContentReferenceIdsToDelete.size > 0 ||
      locallyReorderedContentReferences !== null ||
      contentReferencesToCreate.length > 0 ||
      hasNullSortKeys
    if (onDirtyChange) {
      onDirtyChange(isDirty)
    }
  }, [
    courseModuleContentReferenceIdsToDelete,
    locallyReorderedContentReferences,
    contentReferencesToCreate,
    hasNullSortKeys,
    onDirtyChange,
  ])

  // Handle reset trigger from parent (Cancel button)
  useEffect(() => {
    if (onResetTriggered > 0) {
      setCourseModuleContentReferenceIdsToDelete(new Set())
      setContentReferencesToCreate([])
      setEditedSortKeys({})
      setLocallyReorderedContentReferences(null)
    }
  }, [onResetTriggered])

  // Mutation for deleting content references
  const [deleteContentReference] = useMutation(DELETE_CONTENT_REFERENCE, {
    refetchQueries: [
      {
        query: GET_COURSE_MODULE_CONTENT_REFERENCES,
        variables: { id: moduleId, language: [moduleLanguage, '_primary'] },
      },
    ],
    awaitRefetchQueries: true,
  })

  // Mutation for updating content reference sort order (also handles creation)
  const [createOrUpdateContentReferences] = useMutation(UPDATE_CONTENT_REFERENCES_SORT_ORDER, {
    refetchQueries: [
      {
        query: GET_COURSE_MODULE_CONTENT_REFERENCES,
        variables: { id: moduleId, language: [moduleLanguage, '_primary'] },
      },
    ],
    awaitRefetchQueries: true,
  })

  // Save handler to be called by parent's Save button
  const handleSave = useCallback(async (): Promise<void> => {
    const hasCreations = contentReferencesToCreate.length > 0
    const hasReorder = locallyReorderedContentReferences !== null
    const hasDeletions = courseModuleContentReferenceIdsToDelete.size > 0
    // hasNullSortKeys: repair existing refs in production that have NULL sort_key
    const needsRepair = hasNullSortKeys

    if (!hasCreations && !hasReorder && !hasDeletions && !needsRepair) {
      return
    }

    try {
      // STEP 1: Delete items explicitly
      if (hasDeletions) {
        // Filter out any temporary IDs (shouldn't happen, but safety check)
        const idsToDelete = Array.from(courseModuleContentReferenceIdsToDelete).filter(
          (id) => !id.startsWith('temp-'),
        )

        if (idsToDelete.length > 0) {
          await Promise.all(
            idsToDelete.map((id) =>
              deleteContentReference({
                variables: {
                  contentReference: { id },
                },
              }),
            ),
          )
        }
      }

      // STEP 2: Create ONLY new items with proper sortKeys
      if (hasCreations) {
        // Calculate starting sortKey based on existing content
        const currentList = locallyReorderedContentReferences || serverCourseModuleContentReferences
        const filteredList = currentList.filter(
          (ref: CourseModuleContentReference) =>
            ref.id && !courseModuleContentReferenceIdsToDelete.has(ref.id),
        )
        const maxSortKey = filteredList.reduce((max: number, ref: CourseModuleContentReference) => {
          const sortKey = ref.sortKey !== null && ref.sortKey !== undefined ? ref.sortKey : -1
          return Math.max(max, sortKey)
        }, -1)

        const newItemsToCreate = contentReferencesToCreate.map((item, index) => ({
          fromContentContentId: item.contentReference.contentId,
          fromContentContentType: item.contentReference.contentType,
          toContentContentId: moduleContentId,
          toContentContentType: 'course-module',
          type: item.type,
          sortKey: maxSortKey + 1 + index,
        }))

        await createOrUpdateContentReferences({
          variables: {
            contentReferences: newItemsToCreate,
            deleteOmittedReferences: false,
          },
        })
      }

      // STEP 3: Update sort order of ALL items (existing + newly created from Step 2)
      // Runs on reorder, after creations, after deletions (re-sequence remaining), or to repair NULL sort keys
      if (hasReorder || hasCreations || hasDeletions || needsRepair) {
        // Get the current list (reordered if applicable, otherwise from server)
        // After Step 2's refetch, this includes newly created items
        let finalList = locallyReorderedContentReferences || serverCourseModuleContentReferences

        // Build a set of contentIds that are being created (for temporary ID matching)
        const contentIdsBeingCreated = new Set(
          contentReferencesToCreate.map((item) => item.contentReference.contentId),
        )

        // Filter out deletions and temporary IDs that aren't being created
        finalList = finalList.filter((ref: CourseModuleContentReference) => {
          if (!ref.id) return false
          if (courseModuleContentReferenceIdsToDelete.has(ref.id)) return false

          // If it's a temporary ID, only include it if it's in contentReferencesToCreate
          if (ref.id.startsWith('temp-')) {
            const linkedContent = Array.isArray(ref.contentFrom)
              ? ref.contentFrom[0]
              : ref.contentFrom
            return linkedContent?.contentId && contentIdsBeingCreated.has(linkedContent.contentId)
          }

          return true
        })

        // Build content references for ALL items with final sequential sortKeys
        const allContentReferences = finalList.map(
          (ref: CourseModuleContentReference, index: number) => {
            const linkedContent = Array.isArray(ref.contentFrom)
              ? ref.contentFrom[0]
              : ref.contentFrom

            return {
              fromContentContentId: linkedContent?.contentId,
              fromContentContentType: linkedContent?.contentType,
              toContentContentId: moduleContentId,
              toContentContentType: 'course-module',
              type: ref.contentReferenceType?.type,
              sortKey: index,
            }
          },
        )

        if (allContentReferences.length > 0) {
          await createOrUpdateContentReferences({
            variables: {
              contentReferences: allContentReferences,
              deleteOmittedReferences: false,
            },
          })
        }
      }

      // Clear all pending states after successful save
      setContentReferencesToCreate([])
      setLocallyReorderedContentReferences(null)
      setCourseModuleContentReferenceIdsToDelete(new Set())
    } catch (saveError) {
      const errorMessage = saveError instanceof Error ? saveError.message : 'Unknown error'
      throw new Error(`Error saving course module content references: ${errorMessage}`)
    }
  }, [
    contentReferencesToCreate,
    locallyReorderedContentReferences,
    serverCourseModuleContentReferences,
    courseModuleContentReferenceIdsToDelete,
    hasNullSortKeys,
    moduleContentId,
    deleteContentReference,
    createOrUpdateContentReferences,
  ])

  // Provide save handler to parent
  useEffect(() => {
    if (onSaveHandler) {
      onSaveHandler(handleSave)
    }
  }, [onSaveHandler, handleSave])

  // Handle removing a course module content reference
  const handleRemoveCourseModuleContentReference = (contentReferenceId: string) => {
    // Check if this is a temporary ID (newly added item that hasn't been saved yet)
    if (contentReferenceId.startsWith('temp-')) {
      // For temporary IDs, remove from contentReferencesToCreate and locallyReorderedContentReferences
      // Find the content reference to get its contentId for matching
      const currentList = locallyReorderedContentReferences || serverCourseModuleContentReferences
      const itemToRemove = currentList.find(
        (ref: CourseModuleContentReference) => ref.id === contentReferenceId,
      )

      if (itemToRemove) {
        const linkedContent = Array.isArray(itemToRemove.contentFrom)
          ? itemToRemove.contentFrom[0]
          : itemToRemove.contentFrom

        // Remove from contentReferencesToCreate by matching contentId
        if (linkedContent?.contentId) {
          setContentReferencesToCreate((prev) =>
            prev.filter((item) => item.contentReference.contentId !== linkedContent.contentId),
          )
        }

        // Remove from locallyReorderedContentReferences
        if (locallyReorderedContentReferences) {
          setLocallyReorderedContentReferences((prev) => {
            if (!prev) return null
            const filtered = prev.filter((ref) => ref.id !== contentReferenceId)
            return filtered.length > 0 ? filtered : null
          })
        }
      }
    } else {
      // For real IDs, add to deletion set (existing behavior)
      setCourseModuleContentReferenceIdsToDelete((prev) => new Set(prev).add(contentReferenceId))
    }
  }

  // Handle sort key change
  const handleSortKeyChange = (contentReferenceId: string, value: string) => {
    setEditedSortKeys((prev) => ({
      ...prev,
      [contentReferenceId]: value,
    }))
  }

  // Handle confirm sort key change - confirms the reorder locally (doesn't save yet)
  const handleConfirmSortKey = (contentReferenceId: string) => {
    const newPositionStr = editedSortKeys[contentReferenceId]
    const newPosition = parseInt(newPositionStr, 10) - 1 // Convert from 1-indexed to 0-indexed

    // Use locally reordered content references if available, otherwise use original server list
    const currentList = locallyReorderedContentReferences || serverCourseModuleContentReferences

    // Filter out deleted items
    const filteredList = currentList.filter(
      (ref: CourseModuleContentReference) =>
        ref.id && !courseModuleContentReferenceIdsToDelete.has(ref.id),
    )

    // IMPORTANT: locallyReorderedContentReferences is already in correct order from previous splice()
    // Only sort the original serverCourseModuleContentReferences by their sortKey from the server
    const sortedList = locallyReorderedContentReferences
      ? [...filteredList] // Already ordered correctly - don't sort!
      : [...filteredList].sort((a, b) => {
          const sortKeyA = a.sortKey !== null && a.sortKey !== undefined ? a.sortKey : 0
          const sortKeyB = b.sortKey !== null && b.sortKey !== undefined ? b.sortKey : 0
          return sortKeyA - sortKeyB
        })

    // Validate the new position
    if (Number.isNaN(newPosition) || newPosition < 0 || newPosition >= sortedList.length) {
      // Clear the edit on invalid input
      setEditedSortKeys((prev) => {
        const updated = { ...prev }
        delete updated[contentReferenceId]
        return updated
      })
      return
    }

    // Find current position of the content reference in the sorted list
    const currentPosition = sortedList.findIndex((ref) => ref.id && ref.id === contentReferenceId)

    if (currentPosition === -1) {
      setEditedSortKeys((prev) => {
        const updated = { ...prev }
        delete updated[contentReferenceId]
        return updated
      })
      return
    }

    // If position hasn't changed, just clear the edit
    if (currentPosition === newPosition) {
      setEditedSortKeys((prev) => {
        const updated = { ...prev }
        delete updated[contentReferenceId]
        return updated
      })
      return
    }

    // Reorder the array using splice (Page Manager pattern)
    const reorderedList = [...sortedList]
    const [movedItem] = reorderedList.splice(currentPosition, 1)
    reorderedList.splice(newPosition, 0, movedItem)

    // Update sortKeys to match new positions (0-indexed)
    const reorderedWithSortKeys = reorderedList.map((ref, index) => ({
      ...ref,
      sortKey: index,
    }))

    // Store the reordered content references locally (this triggers dirty state and UI update)
    setLocallyReorderedContentReferences(reorderedWithSortKeys)

    // Clear the edited sort key for this item
    setEditedSortKeys((prev) => {
      const updated = { ...prev }
      delete updated[contentReferenceId]
      return updated
    })
  }

  // Handle cancel sort key change
  const handleCancelSortKey = (contentReferenceId: string) => {
    setEditedSortKeys((prev) => {
      const updated = { ...prev }
      delete updated[contentReferenceId]
      return updated
    })
  }

  // Handle adding content
  const handleAddContent = useCallback(
    (selectedContent?: ContentReference) => {
      // If no content provided, use the selected content from state
      const contentToAdd = selectedContent || selectedContentToAdd[0]
      if (!contentToAdd) {
        return
      }

      // Calculate the next sort key based on existing content
      const currentList = locallyReorderedContentReferences || serverCourseModuleContentReferences
      const filteredList = currentList.filter(
        (ref: CourseModuleContentReference) =>
          ref.id && !courseModuleContentReferenceIdsToDelete.has(ref.id),
      )
      const maxSortKey = filteredList.reduce((max: number, ref: CourseModuleContentReference) => {
        const sortKey = ref.sortKey !== null && ref.sortKey !== undefined ? ref.sortKey : -1
        return Math.max(max, sortKey)
      }, -1)
      const nextSortKey = maxSortKey + 1

      // Create optimistic content reference for immediate UI update
      const optimisticContentReference: CourseModuleContentReference = {
        id: `temp-${Date.now()}`, // Temporary ID
        contentReferenceType: {
          type: contentReferenceTypeToAdd,
        },
        sortKey: nextSortKey,
        contentFrom: [
          {
            id: contentToAdd.id,
            contentId: contentToAdd.contentId,
            contentType: contentToAdd.contentType,
            title: contentToAdd.title,
            published: contentToAdd.published,
            imageContentRelationships: contentToAdd.thumbnailUrl
              ? [
                  {
                    id: `temp-image-${Date.now()}`,
                    type: 'd6-keyart-no-text',
                    image: {
                      id: `temp-image-${Date.now()}`,
                      url: contentToAdd.thumbnailUrl,
                    },
                  },
                ]
              : null,
          },
        ],
      }

      // Add to local reordered list immediately for optimistic UI
      const optimisticList = [...filteredList, optimisticContentReference]
      setLocallyReorderedContentReferences(optimisticList)

      // Add to the list of content references to create (for saving)
      setContentReferencesToCreate((prev) => [
        ...prev,
        {
          contentReference: contentToAdd,
          type: contentReferenceTypeToAdd,
        },
      ])

      // Notify parent component about the added content
      if (onContentAdded) {
        onContentAdded(contentToAdd, contentReferenceTypeToAdd)
      }

      // Reset form immediately for better UX
      setIsAddingContent(false)
      setSelectedContentToAdd([])
      setContentReferenceTypeToAdd(CONTENT_REFERENCE_TYPE_COURSE_MODULE_PRACTICE)
    },
    [
      selectedContentToAdd,
      contentReferenceTypeToAdd,
      onContentAdded,
      locallyReorderedContentReferences,
      serverCourseModuleContentReferences,
      courseModuleContentReferenceIdsToDelete,
    ],
  )

  // Compute the final visible list (server + optimistic, excluding deletions)
  // This is used both for determining whether to show the empty state and for rendering
  const visibleContentReferences = useMemo(() => {
    // Get the current list (either locally reordered or original server list)
    const currentList = locallyReorderedContentReferences || serverCourseModuleContentReferences

    // Filter out deleted items
    const filteredList = currentList.filter(
      (courseModuleContentReference: CourseModuleContentReference) =>
        courseModuleContentReference.id &&
        !courseModuleContentReferenceIdsToDelete.has(courseModuleContentReference.id),
    )

    // IMPORTANT: locallyReorderedContentReferences is already in correct order from handleConfirmSortKey
    // Only sort the original serverCourseModuleContentReferences by their sortKey from the server
    const sortedList = locallyReorderedContentReferences
      ? filteredList // Already ordered correctly - don't sort!
      : filteredList.sort(
          (
            courseModuleContentReferenceA: CourseModuleContentReference,
            courseModuleContentReferenceB: CourseModuleContentReference,
          ) => {
            // Sort by sortKey of the content reference
            // Handle null/undefined by treating as 0, but ensure consistent ordering
            const sortKeyA =
              courseModuleContentReferenceA.sortKey !== null &&
              courseModuleContentReferenceA.sortKey !== undefined
                ? courseModuleContentReferenceA.sortKey
                : 0
            const sortKeyB =
              courseModuleContentReferenceB.sortKey !== null &&
              courseModuleContentReferenceB.sortKey !== undefined
                ? courseModuleContentReferenceB.sortKey
                : 0

            // Primary sort: by sortKey (ascending)
            if (sortKeyA !== sortKeyB) {
              return sortKeyA - sortKeyB
            }

            // Secondary sort: by id for stable ordering when sortKeys are equal
            const idA = courseModuleContentReferenceA.id || ''
            const idB = courseModuleContentReferenceB.id || ''
            return idA.localeCompare(idB)
          },
        )

    // Only include items with IDs
    return sortedList.filter((ref: CourseModuleContentReference) => ref.id)
  }, [
    locallyReorderedContentReferences,
    serverCourseModuleContentReferences,
    courseModuleContentReferenceIdsToDelete,
  ])

  // Handle loading state
  if (loading) {
    return (
      <SectionAccordion title="Course Module Content" collapsed={false}>
        <Alert severity="info">Loading content...</Alert>
      </SectionAccordion>
    )
  }

  // Handle error state
  if (error) {
    return (
      <SectionAccordion title="Course Module Content" collapsed={false}>
        <Alert severity="error">Error loading content: {error.message}</Alert>
      </SectionAccordion>
    )
  }

  return (
    <SectionAccordion title="Module Content" collapsed={false}>
      {visibleContentReferences.length === 0 ? (
        <Alert severity="info">
          No content linked yet. Add practice and other content to this course module.
        </Alert>
      ) : (
        <Box sx={{ mt: 2 }}>
          <Grid container spacing={2}>
            {visibleContentReferences.map(
              (courseModuleContentReference: CourseModuleContentReference, index: number) => {
                // Handle both array and single object structure
                const linkedContent = Array.isArray(courseModuleContentReference.contentFrom)
                  ? courseModuleContentReference.contentFrom[0]
                  : courseModuleContentReference.contentFrom
                const linkedContentTitle = linkedContent?.title || 'Untitled'
                const contentReferenceId = courseModuleContentReference.id!

                // Get the best available image URL
                // Prefer d6-keyart-with-text type, but fall back to d6-keyart-no-text
                const imageContentRelationships = linkedContent?.imageContentRelationships || []
                const keyartWithTextImage = imageContentRelationships.find(
                  (rel: { type: string; image?: { url: string } | null } | null) =>
                    rel?.type === 'd6-keyart-with-text' && rel?.image?.url,
                )
                const keyartNoTextImage = imageContentRelationships.find(
                  (rel: { type: string; image?: { url: string } | null } | null) =>
                    rel?.type === 'd6-keyart-no-text' && rel?.image?.url,
                )
                const imageContentRelationship = keyartWithTextImage || keyartNoTextImage
                const contentThumbnailUrl = imageContentRelationship?.image?.url || null

                const contentReferenceType = courseModuleContentReference.contentReferenceType?.type

                return (
                  <CourseModuleContentCard
                    key={contentReferenceId}
                    contentId={linkedContent?.id}
                    contentTitle={linkedContentTitle}
                    contentIdNumber={linkedContent?.contentId}
                    contentType={linkedContent?.contentType}
                    contentReferenceType={contentReferenceType}
                    thumbnailUrl={contentThumbnailUrl}
                    published={linkedContent?.published}
                    referenceId={contentReferenceId}
                    sortOrder={index + 1}
                    editedSortKey={editedSortKeys[contentReferenceId]}
                    disabled={disabled}
                    onSortKeyChange={handleSortKeyChange}
                    onConfirmSortKey={handleConfirmSortKey}
                    onCancelSortKey={handleCancelSortKey}
                    onRemove={handleRemoveCourseModuleContentReference}
                  />
                )
              },
            )}
          </Grid>
        </Box>
      )}

      {/* Add Content Section */}
      {!disabled && (
        <>
          {!isAddingContent ? (
            <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={handleAddPracticeClick}
                sx={{ flex: 1 }}
              >
                Add Course Module Practice
              </Button>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={handleAddOtherClick}
                sx={{ flex: 1 }}
              >
                Add Course Module Other
              </Button>
            </Box>
          ) : (
            <Box className="course-module-content-section__add-content-form">
              <ContentAutocomplete
                value={selectedContentToAdd}
                onChange={setSelectedContentToAdd}
                onSelect={handleAddContent}
                label="Search content"
                disabled={disabled}
              />
            </Box>
          )}
        </>
      )}
    </SectionAccordion>
  )
}

export default CourseModuleContentSection
